import type { DataOption } from '@/entities/column'
import type { FieldDefinition } from '@/shared/components/query-builder'
import type { FilterRuleModel } from '@/shared/generated'
import type { TreeNodeType } from '@/shared/lib/tree'
import type { UseControllerProps } from 'react-hook-form'

import React, { useState } from 'react'
import { EyeIcon, EyeOffIcon, HelpCircleIcon, PlusIcon } from 'lucide-react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { formatFilterRule } from '@/entities/column'
import { isSkuRule } from '@/entities/scenario'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { getLocalization } from '@/shared/components/filter/localization'
import {
	getFiledConfig,
	QueryBuilder,
	QueryBuilderAddButton,
	QueryBuilderEmpty,
	QueryBuilderRulesList,
} from '@/shared/components/query-builder'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import {
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
} from '@/shared/components/ui/form'
import { Label } from '@/shared/components/ui/label'
import { LinkButton } from '@/shared/components/ui/link-button'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { useAppStore } from '@/shared/contexts/app'

export default function RHFStoplistCustomRules({
	name,
	label,
	hint,
	description,
	rules,
	shouldUnregister,
	defaultValue,
	fields,
	dataChoices,
	isLoading,
}: UseControllerProps & {
	isLoading?: boolean
	label?: string
	hint?: string
	description?: string
	rules?: FilterRuleModel[]
	fields: FieldDefinition[]
	dataChoices: Record<string, DataOption[] | TreeNodeType[]>
}) {
	const { t } = useTranslation()
	const dateFormat = useAppStore((state) => state.dateFormat)
	const { control } = useFormContext()
	const [isAllShown, setIsAllShown] = useState(false)

	return (
		<FormField
			name={name}
			rules={rules || []}
			shouldUnregister={shouldUnregister}
			defaultValue={defaultValue}
			control={control}
			render={({ field, fieldState: { error } }) => {
				const skuRules = field.value?.filter(isSkuRule) || []
				const notSkuRules =
					field.value?.filter((item: FilterRuleModel) => !isSkuRule(item)) || []

				return (
					<FormItem>
						{!!label && (
							<FormLabel>
								{label}
								{hint && (
									<Tooltip>
										<TooltipTrigger asChild>
											<HelpCircleIcon className='size-3 text-muted-foreground' />
										</TooltipTrigger>
										<TooltipContent side='top'>{hint}</TooltipContent>
									</Tooltip>
								)}
							</FormLabel>
						)}
						{!!description && <FormDescription>{description}</FormDescription>}

						{isLoading ? (
							<div className='flex flex-col space-y-2'>
								<Skeleton className='h-10 w-full' />
								<Skeleton className='h-10 w-full' />
							</div>
						) : (
							<ErrorBoundary
								fallback={() => (
									<Alert variant='destructive'>
										<AlertDescription>
											🐞{' '}
											{t('ui_fragment_error', {
												defaultValue:
													'Oops ... it looks like an unexpected error has occurred. Our team`s been notified.',
											})}
										</AlertDescription>
									</Alert>
								)}
							>
								<div className='flex items-center justify-between'>
									<Label>{t('app.filters')}</Label>
									{!!skuRules?.length && isAllShown ? (
										<LinkButton
											variant='brand'
											size='sm'
											onClick={() => setIsAllShown(false)}
										>
											<EyeIcon />
											{t('hide_sku_rule', {
												value: skuRules.length,
												defaultValue: `Hide ${skuRules?.length} SKU rules`,
											})}
										</LinkButton>
									) : (
										<LinkButton
											variant='brand'
											size='sm'
											onClick={() => setIsAllShown(true)}
										>
											<EyeOffIcon />
											{t('show_sku_rule', {
												value: skuRules.length,
												defaultValue: `Show ${skuRules.length} SKU rules`,
											})}
										</LinkButton>
									)}
								</div>
								<QueryBuilder
									dateFormat={dateFormat}
									enableReferencing
									errorSchema={error as unknown as any[]}
									rules={isAllShown ? field.value : notSkuRules}
									onRulesChange={(rules) =>
										field.onChange(
											(isAllShown ? rules : [...skuRules, ...rules]).map(
												formatFilterRule
											)
										)
									}
									fieldConfig={getFiledConfig(fields, dataChoices)}
									locale={getLocalization(t)}
								>
									<QueryBuilderEmpty className='border-b' />
									<QueryBuilderRulesList
										defaultJoinOperator='or'
										className='max-h-100 overflow-y-auto border-y py-2'
									/>
									{!!error && (
										<p
											data-slot='form-message'
											className='text-sm text-destructive'
										>
											{error?.message || t('validation.invalid_rules')}
										</p>
									)}
									<div>
										<QueryBuilderAddButton
											data-testid='filters-popover-add'
											className='mr-auto'
										>
											<PlusIcon />
											{t('filters_add_filter')}
										</QueryBuilderAddButton>
									</div>
								</QueryBuilder>
							</ErrorBoundary>
						)}
					</FormItem>
				)
			}}
		/>
	)
}
